// frontend/src/pages/learningpage.js
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/languagecontext';
import LearningPageHeader from './learningpageheader';
import LearningPageContent from './learningpagecontent';
import LearningPageActions from './learningpageactions';
import LearningPageNavigation from './learningpagenavigation';
import RichContentDisplay from './richcontentdisplay';
import { groupBlocks } from './richcontenthelpers.js';
import { renderBlock } from './richcontenthelpers.js';

const LearningPage = ({
  currentChunk,
  currentMicroChunk,
  currentMicroIndex,
  passedSubject,
  passedChapter,
  passedTopic,
  passedSubtopic,
  goToPreviousMicroChunk,
  goToNextMicroChunk,
  backToChunks
}) => {
  const { currentLanguage, switchLanguage } = useLanguage();

  // ── Shared active section state ───────────────────────────────────────────
  const [activeSection,  setActiveSection]  = useState(null);

  // ── Blocks state — lifted up from RichContentDisplay ─────────────────────
  const [richBlocks,     setRichBlocks]     = useState([]);
  const [groupedBlocks,  setGroupedBlocks]  = useState([]);
  const [blocksReady,    setBlocksReady]    = useState(false);

  // ── Reset when micro chunk changes ────────────────────────────────────────
  useEffect(() => {
    setActiveSection(null);
    setRichBlocks([]);
    setGroupedBlocks([]);
    setBlocksReady(false);
  }, [currentMicroChunk?.micro_chunk_id]);

  // ── When blocks load, group them ─────────────────────────────────────────
  const handleBlocksLoaded = (blocks) => {
    setRichBlocks(blocks);
    setGroupedBlocks(groupBlocks(blocks));
    setBlocksReady(true);
  };

  const handleSectionToggle = (section) => {
    setActiveSection(prev => prev === section ? null : section);
  };

  return (
    <div style={{
      background     : 'white',
      borderRadius   : '12px',
      boxShadow      : '0 2px 10px rgba(0, 0, 0, 0.1)',
      overflow       : 'hidden',
      minHeight      : '80vh'
    }}>
      {/* Header */}
      <LearningPageHeader
        currentLanguage={currentLanguage}
        switchLanguage={switchLanguage}
        currentMicroIndex={currentMicroIndex}
        totalMicroChunks={currentChunk?.micro_chunks?.length || 0}
        backToChunks={backToChunks}
      />

      {/* Main Content */}
      <div style={{ padding: '0 20px 20px 20px' }}>

        {/* Micro chunk text */}
        <LearningPageContent
          currentChunk={currentChunk}
          currentMicroChunk={currentMicroChunk}
        />

        {/* Always-visible blocks — tables, text, flowchart, html */}
        {groupedBlocks
          .filter(group =>
            !group.group_label &&
            group.blocks.every(b => 
              b.block_type === 'table' || 
              b.block_type === 'text' ||
              b.block_type === 'flowchart' ||
              b.block_type === 'html'
            )
          )
          .map(group => (
            <div key={group.key} className="rc-always-visible">
              {group.blocks.map(block => renderBlock(block))}
            </div>
          ))
        }

        {/* Data fetcher — renders nothing, just loads blocks */}
        {currentMicroChunk?.micro_chunk_id && (
          <RichContentDisplay
            microChunkId={currentMicroChunk.micro_chunk_id}
            language={currentLanguage}
            onBlocksLoaded={handleBlocksLoaded}
          />
        )}

        {/* Unified actions — all buttons + all content */}
        <LearningPageActions
          currentMicroChunk={currentMicroChunk}
          currentLanguage={currentLanguage}
          passedSubject={passedSubject}
          passedChapter={passedChapter}
          passedTopic={passedTopic}
          passedSubtopic={passedSubtopic}
          currentChunk={currentChunk}
          activeSection={activeSection}
          onSectionToggle={handleSectionToggle}
          groupedBlocks={groupedBlocks}
          blocksReady={blocksReady}
        />

        {/* Navigation */}
        <LearningPageNavigation
          currentMicroIndex={currentMicroIndex}
          totalMicroChunks={currentChunk?.micro_chunks?.length || 0}
          goToPreviousMicroChunk={goToPreviousMicroChunk}
          goToNextMicroChunk={goToNextMicroChunk}
        />
      </div>
    </div>
  );
};

export default LearningPage;